import { ReactNode } from "react";
import {
  components,
  DropdownIndicatorProps,
  InputProps,
  MenuListProps,
  MultiValueProps,
  OptionProps,
  PlaceholderProps,
  SingleValueProps,
} from "react-select";
import { List, RowComponentProps } from "react-window";
import cn from "classnames";
import { OptionType, SelectProps } from "shared/ui/CustomSelect/model/customSelect.types";
import CustomCheckbox from "shared/ui/CustomCheckbox/CustomCheckbox";
import { AngleUpIcon, CheckIcon } from "shared/ui/Icons";
import styles from "shared/ui/CustomSelect/ui/CustomSelect.module.scss";

export const CustomDropdownIndicator = <T extends OptionType>({ className, ...props }: DropdownIndicatorProps<T>) => {
  return (
    <components.DropdownIndicator {...props}>
      <div className={cn(styles.dropDownIndicator, className)}>
        <AngleUpIcon />
      </div>
    </components.DropdownIndicator>
  );
};

export const CustomSingleValue = <T extends OptionType>({ className, ...props }: SingleValueProps<T>) => {
  return (
    <components.SingleValue {...props}>
      <div className={cn(styles.singleValue, className)}>
        {props.data.icon && <span className={styles.valueIcon}>{props.data.icon}</span>}
        {props.data.label && <span className={styles.valueLabel}>{props.data.label}</span>}
      </div>
    </components.SingleValue>
  );
};

export const CustomMultiValue = <T extends OptionType>({ index, getValue, className }: MultiValueProps<T>) => {
  return !index && <div className={cn(styles.multiValue, className)}>{getValue().length} items selected</div>;
};

type CustomOptionProps<T extends OptionType> = OptionProps<T> & {
  selectedIndicator: NonNullable<SelectProps<T>["selectedIndicator"]>;
};

export const CustomOption = <T extends OptionType>({ className, ...props }: CustomOptionProps<T>) => {
  const { isSelected, isFocused, selectedIndicator } = props;
  const selectedIndicatorMap: Record<CustomOptionProps<T>["selectedIndicator"], ReactNode> = {
    checkmark: isSelected && <CheckIcon />,
    checkbox: <CustomCheckbox checked={isSelected} />,
    none: null,
  };

  return (
    <components.Option {...props}>
      <div
        className={cn(styles.option, { [styles.isSelected]: isSelected }, { [styles.isFocused]: isFocused }, className)}
      >
        <div className={styles.textWrap}>
          {props.data.icon && <span className={styles.valueIcon}>{props.data.icon}</span>}
          {props.data.label && <span className={styles.valueLabel}>{props.data.label}</span>}
        </div>
        {selectedIndicatorMap[selectedIndicator]}
      </div>
    </components.Option>
  );
};

export const CustomPlaceholder = <T extends OptionType>({
  children,
  className,
  dataTestId,
  ...props
}: PlaceholderProps<T> & { dataTestId?: string }) => {
  const { selectProps } = props;

  if (selectProps.menuIsOpen && selectProps.isSearchable) {
    return null;
  }

  return (
    <components.Placeholder {...props}>
      <div data-test-id={dataTestId} className={cn(styles.placeholder, className)}>
        {children}
      </div>
    </components.Placeholder>
  );
};

export const CustomInput = <T extends OptionType>({ className, ...props }: InputProps<T>) => {
  const { isMulti, getValue } = props;

  return (
    <components.Input
      {...props}
      autoComplete="off"
      className={cn(
        styles.input,
        { [styles.isMulti]: isMulti },
        { [styles.isNonEmpty]: !!getValue().length },
        className
      )}
    />
  );
};

const isReactNodeArray = (value: ReactNode): value is ReactNode[] => Array.isArray(value);

type MenuListRowData = { childrenArray: ReactNode[] };

const MenuListRow = ({ index, style, childrenArray }: RowComponentProps<MenuListRowData>) => (
  <div style={style}>{childrenArray[index]}</div>
);

export const CustomLazyLoadedMenuList = <T extends OptionType>({
  className,
  lazyLoadedMenuListRowHeight = 38, // 38px is the height of the option
  ...props
}: MenuListProps<T> & { lazyLoadedMenuListRowHeight?: number }) => {
  const { children, maxHeight } = props;
  const childrenArray = isReactNodeArray(children) ? children : [];

  return (
    <div className={cn(styles.menuList, className)} style={{ height: maxHeight }}>
      <List
        rowCount={childrenArray.length}
        rowHeight={lazyLoadedMenuListRowHeight}
        rowProps={{ childrenArray }}
        rowComponent={MenuListRow}
      />
    </div>
  );
};

export const CustomMenuList = <T extends OptionType>({ className, ...props }: MenuListProps<T>) => {
  return <components.MenuList {...props} className={className} />;
};
